import { CommandGroup, CommandItem } from '@discover-ui/command';
import CheckIcon from '@discover-ui/icons/check.svg';
import PlusIcon from '@discover-ui/icons/plus.svg';
import { cn } from '@discover-lib/utils';

type CR257ChipOption = {
  key: string;
  id: number;
  label: string;
  search: string;
};

export type CR257ChipOptionGroup = {
  header: { key: string; label: string } | null;
  items: CR257ChipOption[];
};

type CR257ChipOptionListProps = {
  groups: CR257ChipOptionGroup[];
  selectedIds: Set<number>;
  isAtMax: boolean;
  onToggle: (id: number) => void;
};

function CR257ChipOptionItem({
  option,
  isSelected,
  isDisabled,
  onToggle,
}: {
  option: CR257ChipOption;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: (id: number) => void;
}) {
  return (
    <CommandItem
      value={option.search}
      disabled={isDisabled}
      onSelect={() => onToggle(option.id)}
      className={cn(
        'inline-flex w-fit gap-1.5 rounded-full px-3 py-1.5 text-sm',
        // cmdk leaves aria-selected on the last tapped item; match the resting colour so it cannot stick.
        isSelected
          ? 'border border-ds-brand-300 bg-ds-brand-200 font-medium text-ds-brand-700 aria-selected:bg-ds-brand-200 aria-selected:text-ds-brand-700 pointer-fine:hover:bg-ds-brand-300 pointer-fine:hover:text-ds-brand-700 pointer-fine:aria-selected:hover:bg-ds-brand-300 pointer-fine:aria-selected:hover:text-ds-brand-700'
          : 'bg-ds-gray-light-50 text-ds-gray-light-950 aria-selected:bg-ds-gray-light-50 aria-selected:text-ds-gray-light-950 pointer-fine:hover:bg-ds-gray-light-100 pointer-fine:hover:text-ds-gray-light-950 pointer-fine:aria-selected:hover:bg-ds-gray-light-100 pointer-fine:aria-selected:hover:text-ds-gray-light-950'
      )}>
      <span className={cn('max-w-48 truncate', isSelected && 'text-ds-brand-900')}>
        {option.label}
      </span>
      {isSelected ? (
        <CheckIcon className="size-3.5 shrink-0" />
      ) : (
        <PlusIcon className="size-3.5 shrink-0" />
      )}
    </CommandItem>
  );
}

export default function CR257ChipOptionList({
  groups,
  selectedIds,
  isAtMax,
  onToggle,
}: CR257ChipOptionListProps) {
  return groups.map((group) => (
    <CommandGroup
      key={group.header?.key ?? group.items[0]?.key ?? 'ungrouped'}
      heading={group.header?.label}
      className={cn('p-0 [&_[cmdk-group-heading]]:px-0', group.header && 'mt-3')}>
      <div className="flex flex-wrap gap-2">
        {group.items.map((option) => {
          const isSelected = selectedIds.has(option.id);
          return (
            <CR257ChipOptionItem
              key={option.key}
              option={option}
              isSelected={isSelected}
              isDisabled={!isSelected && isAtMax}
              onToggle={onToggle}
            />
          );
        })}
      </div>
    </CommandGroup>
  ));
}
